import { Link, useParams } from "react-router-dom";
import { useGroq } from "../lib/sanity";
import { imageIdToURL } from "../utils/LinkUtils";
import { useSeo } from "../utils/useSeo";

const TEAM_QUERY = `{
  "team": *[_type == "team" && slug.current == $slug][0]{
    "id": _id,
    name,
    suffix,
    "slug": slug.current,
    "game": game->{ name, icon },
    "members": *[_type == "teamMember" && team._ref == ^._id]{
      "id": _id,
      username,
      rank,
      position,
      "profile": profile->{
        "id": _id,
        "first_name": firstName,
        "last_name": lastName,
        avatar
      }
    }
  }
}`;

const RankMap: { [key: string]: string } = {
  captain: "Captain",
  assistant_captain: "Assistant Captain",
  igl: "IGL",
  player: "Player",
  substitute: "Substitute",
  reserve: "Reserve",
  coach: "Coach",
};

const TeamDetail = () => {
  const { slug } = useParams();
  const { loading, error, data } = useGroq<any>(TEAM_QUERY, {
    slug: slug || "",
  });
  const team = data?.team;
  useSeo({
    title: team?.name || team?.game?.name || "Team",
    description: team
      ? `Roster and details for the Brown Esports ${
          team.game?.name || team.name
        } team.`
      : undefined,
  });

  if (!loading && !error && !team) {
    return (
      <div className="bg-gray-900 text-gray-100 min-h-screen">
        <div className="container mx-auto px-8 pt-28 md:pt-36 pb-16 text-center">
          <h1 className="font-header font-bold text-4xl mb-4">Team not found</h1>
          <Link to="/teams" className="text-red-400 hover:underline">
            ← Back to all teams
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-900 text-gray-100 min-h-screen">
      <div className="container mx-auto px-8 pt-28 md:pt-36 pb-16">
        {team ? (
          <>
            <div className="flex items-center mb-12">
              <div className="bg-gray-800 w-20 h-20 md:w-24 md:h-24 rounded-lg mr-6 flex-shrink-0 flex items-center justify-center">
                <img
                  src={imageIdToURL(team.game?.icon)}
                  alt={(team.game?.name || "Game") + " logo"}
                  className="w-14 h-14 object-contain"
                />
              </div>
              <div>
                <h1 className="font-header font-bold text-4xl md:text-6xl text-white">
                  {team.game?.name || team.name}
                </h1>
                {team.suffix ? (
                  <div className="text-xl text-gray-400 font-body">
                    {team.suffix}
                  </div>
                ) : null}
              </div>
            </div>

            <div className="font-bold font-header text-2xl md:text-3xl uppercase tracking-wider mb-6">
              Roster
            </div>

            {team.members && team.members.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6 max-w-5xl">
                {team.members.map((m: any) => (
                  <RosterCard key={m.id} member={m} />
                ))}
              </div>
            ) : (
              <div className="text-gray-400">Roster coming soon.</div>
            )}

            <div className="mt-12">
              <Link to="/teams" className="text-red-400 hover:underline">
                ← All teams
              </Link>
            </div>
          </>
        ) : (
          <div className="text-center text-gray-400">Loading…</div>
        )}
      </div>
    </div>
  );
};

const RosterCard = (props: { member: any }) => {
  const m = props.member;
  const p = m.profile;
  const card = (
    <div className="bg-gray-800 rounded-lg overflow-hidden hover:bg-gray-750 transition-all duration-75 h-full">
      <img
        src={imageIdToURL(p?.avatar)}
        alt={p ? [p.first_name, p.last_name].filter(Boolean).join(" ") : "Player"}
        className="object-cover aspect-square w-full"
      />
      <div className="p-3">
        {m.username ? (
          <div className="font-bold font-header text-white tracking-wide truncate">
            {m.username}
          </div>
        ) : null}
        <div className="text-sm text-gray-300 truncate">
          {p ? [p.first_name, p.last_name].filter(Boolean).join(" ") : ""}
        </div>
        {m.rank && m.rank.length > 0 ? (
          <div className="text-xs text-gray-400 mt-1">
            {m.rank.map((r: string) => RankMap[r] || r).join(", ")}
          </div>
        ) : m.position ? (
          <div className="text-xs text-gray-400 mt-1">{m.position}</div>
        ) : null}
      </div>
    </div>
  );
  return p?.id ? <Link to={`/profile/${p.id}`}>{card}</Link> : card;
};

export default TeamDetail;
